import styled, { keyframes, css } from 'styled-components'
import type {
  AutosaveIndicatorPosition,
  AutosaveStatus,
} from './AutosaveIndicator.types'

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(8px);
  }
`

const positionStyles: Record<
  AutosaveIndicatorPosition,
  ReturnType<typeof css>
> = {
  'bottom-right': css`
    bottom: 20px;
    right: 20px;
  `,
  'bottom-left': css`
    bottom: 20px;
    left: 20px;
  `,
  'top-right': css`
    top: 80px;
    right: 20px;
  `,
  'top-left': css`
    top: 80px;
    left: 20px;
  `,
}

export const Container = styled.div<{
  $position: AutosaveIndicatorPosition
  $isVisible: boolean
  $isHiding: boolean
}>`
  position: fixed;
  ${({ $position }) => positionStyles[$position]}
  z-index: ${({ theme }) => theme.zIndex.toast};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  animation: ${({ $isVisible, $isHiding }) =>
    $isHiding
      ? css`
          ${fadeOut} 0.3s ease-out forwards
        `
      : $isVisible
        ? css`
            ${fadeIn} 0.3s ease-out
          `
        : 'none'};
  pointer-events: ${({ $isHiding }) => ($isHiding ? 'none' : 'auto')};

  /* Never bleed into printed output: a fixed-position element is stamped onto
     the first printed page, so the "Saving..." pill landed on report covers. */
  @media print {
    display: none !important;
  }
`

const getStatusColor = (
  status: AutosaveStatus,
  colors: {
    primary: string
    success: string
    danger: string
    text: { secondary: string }
  }
): string => {
  switch (status) {
    case 'pending':
      return colors.text.secondary
    case 'saving':
      return colors.primary
    case 'saved':
      return colors.success
    case 'error':
      return colors.danger
    default:
      return 'transparent'
  }
}

export const StatusIcon = styled.span<{ $status: AutosaveStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${({ theme, $status }) => getStatusColor($status, theme.colors)};
`

export const PulsingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
  animation: ${pulse} 1.5s ease-in-out infinite;
`

export const Spinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

export const CheckIcon = styled.svg`
  width: 16px;
  height: 16px;
`

export const WarningIcon = styled.svg`
  width: 16px;
  height: 16px;
`

const getStatusTextColor = (
  status: AutosaveStatus,
  colors: {
    success: string
    danger: string
    text: { secondary: string }
  }
): string => {
  if (status === 'error') return colors.danger
  if (status === 'saved') return colors.success
  return colors.text.secondary
}

export const StatusText = styled.span<{ $status: AutosaveStatus }>`
  color: ${({ theme, $status }) => getStatusTextColor($status, theme.colors)};
  white-space: nowrap;
`

export const RetryButton = styled.button`
  margin-left: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => `${theme.spacing.micro} ${theme.spacing.small}`};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.danger};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.danger};
    color: ${({ theme }) => theme.colors.text.inverse};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`
